import React from "react";
const D = window.SiteData;
/* =========================================================
   珂玺资本 — Studio (AI research) + BP submission
   ========================================================= */

const { useState: useStateS, useRef: useRefS } = React;

// 与后端 UPLOAD_MIME 对齐（pdf / office / 音频），同 pages-dashboard 的 ACCEPT
const ACCEPT_S = ".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.mp3,.wav,.m4a,.aac,.ogg,.flac";

// =========================================================
// STUDIO — 珂玺智库
// =========================================================
function StudioPage({ lang, onLoginClick }) {
  const t = (keyOrCn, en) => {
    if (typeof keyOrCn === "string" && keyOrCn.indexOf(".") > 0 && window.I18N) {
      const [ns, k] = keyOrCn.split(".", 2);
      const val = window.I18N[lang] && window.I18N[lang][ns] && window.I18N[lang][ns][k];
      if (val !== undefined) return val;
    }
    // fallback: 兼容旧 t(cn, en) 调用
    return lang === "cn" ? keyOrCn : (en !== undefined ? en : keyOrCn);
  };
  const s = D.studio;
  const si18 = D.i18nStudio[lang];
  const isZh = lang === "cn";
  const { user, logout } = window.useAuth();
  const [files, setFiles] = useStateS([]);
  const [title, setTitle] = React.useState("");
  const [note, setNote] = React.useState("");
  const [dragging, setDragging] = useStateS(false);
  const [submitState, setSubmitState] = React.useState({ busy: false, ok: null, error: null });
  const [myReports, setMyReports] = React.useState(null);
  const [mySubs, setMySubs] = React.useState(null);
  const fileRef = useRefS(null);

  // 保留真实 File 对象用于上传。
  // ⚠️ 必须先取件再清 input：e.target.files 是 live FileList，React 18 下
  // setFiles 的 updater 延迟执行，若先 value=""，updater 跑时 FileList 已被清空。
  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const dropped = Array.from(e.dataTransfer.files);
    setFiles(prev => [...prev, ...dropped]);
  };
  const onPick = (e) => {
    const picked = Array.from(e.target.files);
    e.target.value = "";
    setFiles(prev => [...prev, ...picked]);
  };
  const fmtSize = (b) => b > 1e6 ? (b / 1e6).toFixed(1) + " MB" : (b / 1024).toFixed(0) + " KB";

  // 我的报告 / 我的提交
  const loadMine = React.useCallback(() => {
    if (!user) { setMyReports(null); setMySubs(null); return; }
    window.authFetch("/api/studio/reports").then(r => r.ok ? r.json() : []).then(setMyReports).catch(() => {});
    window.authFetch("/api/studio/submissions").then(r => r.ok ? r.json() : []).then(setMySubs).catch(() => {});
  }, [user && user.id]);
  React.useEffect(loadMine, [loadMine]);

  // 提交资料
  const submitFiles = async () => {
    if (!user) { onLoginClick(); return; }
    if (files.length === 0) return;
    if (!title.trim()) { setSubmitState({ busy: false, ok: null, error: isZh ? "请填写报告标题" : "Title required" }); return; }
    if (!note.trim()) { setSubmitState({ busy: false, ok: null, error: isZh ? "请填写附言" : "Note required" }); return; }
    setSubmitState({ busy: true, ok: null, error: null });
    try {
      const fd = new FormData();
      files.forEach(f => fd.append("files", f));
      fd.append("title", title.trim());
      fd.append("note", note.trim());
      const res = await window.authFetch("/api/studio/submissions", { method: "POST", body: fd });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = data.error === "unsupported_mime"
          ? (isZh ? `不支持的文件类型：${data.name || ""}` : `Unsupported file type: ${data.name || ""}`)
          : data.error === "file_too_large"
            ? (isZh ? "单个文件不能超过 50 MB" : "Each file must be under 50 MB")
            : data.error === "title_required" ? (isZh ? "标题为必填" : "Title required")
            : data.error === "note_required" ? (isZh ? "附言为必填" : "Note required")
            : (isZh ? "提交失败，请稍后再试" : "Submission failed — please retry");
        setSubmitState({ busy: false, ok: null, error: msg });
        return;
      }
      setFiles([]);
      setTitle("");
      setNote("");
      setSubmitState({ busy: false, ok: data.id, error: null });
      loadMine();
    } catch (err) {
      setSubmitState({ busy: false, ok: null, error: String(err) });
    }
  };

  // 报告可见性：申请公开 / 撤回
  const setReportVis = async (id, action) => {
    const res = await window.authFetch(`/api/studio/reports/${id}/visibility`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action }),
    });
    if (res.ok) loadMine();
  };

  // 公开展示区（无需登录）
  const [showcase, setShowcase] = React.useState(null);
  React.useEffect(() => {
    fetch("/api/studio/showcase")
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (Array.isArray(d)) setShowcase(d); })
      .catch(() => {});
  }, []);

  return (
    <div className="page" data-screen-label="06 Studio">
      {/* Hero */}
      <section style={{ paddingTop: "calc(var(--nav-h) + 120px)", paddingBottom: 80, position: "relative", overflow: "hidden" }}>
        <div style={{
          position: "absolute", right: "-80px", top: "8%",
          fontFamily: "var(--f-serif-en)", fontStyle: "italic",
          fontSize: 360, color: "transparent",
          WebkitTextStroke: "1px rgba(201,168,107,0.08)",
          pointerEvents: "none",
        }}>Studio</div>
        <div className="container-wide" style={{ position: "relative" }}>
          <div className="eyebrow" style={{ marginBottom: 24 }}>{t('studio.t001')}</div>
          <h1 className="h-display">{t('studio.t002')}</h1>
          <h1 className="h-display">
            {lang === "cn"
              ? <span style={{ color: "var(--gold)" }}>{t('studio.t046')}</span>
              : <span className="serif-en" style={{ color: "var(--gold)" }}>{t('studio.t046')}</span>}
          </h1>
          <div className="serif-en" style={{ color: "var(--gold)", fontSize: 22, marginTop: 16 }}>
            {lang === "cn" ? s.tagline.cn : s.tagline.en}
          </div>
          <p className="body-lg body-mute" style={{ maxWidth: 720, marginTop: 36 }}>
            {lang === "cn" ? s.intro.cn : s.intro.en}
          </p>
          {s.intelligence?.url && (
            <div style={{ marginTop: 40, display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
              <a
                className="btn btn-ghost"
                style={{ textDecoration: "none" }}
                href={s.intelligence.url}
                target="_blank" rel="noopener noreferrer"
                data-cms-link="site_data.studio.intelligence.url"
              >
                {lang === "cn" ? s.intelligence.label.cn : s.intelligence.label.en}
                <span className="arr"></span>
              </a>
              <span className="mono" style={{ color: "var(--mute-2)", fontSize: 11, letterSpacing: "0.08em" }}>
                {lang === "cn" ? s.intelligence.note.cn : s.intelligence.note.en}
              </span>
            </div>
          )}
        </div>
      </section>

      {/* Stats */}
      <section style={{ paddingBottom: 80 }}>
        <div className="container-wide">
          <div className="stat-grid">
            {s.stats.map((st, i) => (
              <div className="stat-cell" key={i}>
                <div className="index">{String(i + 1).padStart(2, "0")}</div>
                <div className="stat-num"><Counter to={st.num} suffix={st.suffix} numFixed={`site_data.studio.stats.${i}.num`} suffixFixed={`site_data.studio.stats.${i}.suffix`} /></div>
                <div className="stat-label">{lang === "cn" ? st.cn : st.en}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="section" style={{ background: "var(--bg-0)" }}>
        <div className="container-wide">
          <SectionHead
            lang={lang}
            num={t('studio.num01')}
            kicker={t('studio.k1')}
            title={t('studio.t003')}
            lead={t('studio.t004')}
          />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 0, borderTop: "1px solid var(--line)", borderLeft: "1px solid var(--line)" }}>
            {s.steps.map((step, i) => (
              <div key={step.num} style={{
                padding: 36,
                borderRight: "1px solid var(--line)",
                borderBottom: "1px solid var(--line)",
                minHeight: 280,
                display: "flex", flexDirection: "column", justifyContent: "space-between",
                transition: "background .3s",
              }}
              onMouseEnter={e => e.currentTarget.style.background = "rgba(201,168,107,0.04)"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              >
                <div className="mono" style={{ color: "var(--gold)", letterSpacing: "0.14em" }}>{t('studio.step' + (i + 1))}</div>
                <div>
                  <div style={{ fontFamily: "var(--f-serif-cn)", fontSize: 24, fontWeight: 500 }}>{lang === "cn" ? step.cn : step.en}</div>
                  <p className="body-mute" style={{ marginTop: 18, fontSize: 13, lineHeight: 1.7 }}>{lang === "cn" ? step.body.cn : step.body.en}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Upload + my reports */}
      <section className="section">
        <div className="container-wide">
          <SectionHead
            lang={lang}
            num={t('studio.num02')}
            kicker={t('studio.k2')}
            title={t('studio.t005')}
            lead={t('studio.t006')}
          />

          <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 40 }}>
            {/* Upload zone */}
            <div>
              <div
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={onDrop}
                onClick={() => fileRef.current?.click()}
                style={{
                  border: `1px dashed ${dragging ? "var(--gold)" : "var(--line)"}`,
                  background: dragging ? "rgba(201,168,107,0.04)" : "transparent",
                  padding: 48,
                  minHeight: 320,
                  display: "flex", flexDirection: "column",
                  alignItems: "center", justifyContent: "center",
                  cursor: "pointer",
                  transition: "all .3s",
                  position: "relative",
                }}>
                <input ref={fileRef} type="file" multiple style={{ display: "none" }} accept={ACCEPT_S} onChange={onPick} />
                <div style={{
                  width: 64, height: 64,
                  border: "1px solid var(--gold)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  marginBottom: 28,
                  color: "var(--gold)",
                }}>
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <path d="M14 4v14M7 11l7-7 7 7M4 22h20" stroke="currentColor" strokeWidth="1.2"/>
                  </svg>
                </div>
                <div style={{ fontFamily: "var(--f-serif-cn)", fontSize: 22, marginBottom: 12 }}>{si18.uploadTitle}</div>
                <div className="mono" style={{ color: "var(--mute)", letterSpacing: "0.06em", textAlign: "center", maxWidth: 380, lineHeight: 1.6 }}>
                  {si18.uploadHint}
                </div>
                <button className="btn btn-ghost" style={{ marginTop: 32 }} onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }}>
                  {si18.uploadCta}
                </button>
              </div>

              {/* File list */}
              {files.length > 0 && (
                <div style={{ marginTop: 28, border: "1px solid var(--line)", padding: 24 }}>
                  <div className="eyebrow-mute" style={{ marginBottom: 16 }}>{t("已选文件 · " + files.length, "Selected · " + files.length)}</div>
                  {files.map((f, i) => (
                    <div key={i} style={{
                      display: "grid", gridTemplateColumns: "auto 1fr auto auto", gap: 16,
                      alignItems: "center",
                      padding: "12px 0",
                      borderBottom: i < files.length - 1 ? "1px solid var(--line-2)" : "none",
                    }}>
                      <span className="mono" style={{ color: "var(--gold)" }}>↑</span>
                      <span style={{ fontSize: 14, color: "var(--ivory)" }}>{f.name}</span>
                      <span className="mono" style={{ color: "var(--mute-2)", fontSize: 11 }}>{fmtSize(f.size)}</span>
                      <button onClick={(e) => { e.stopPropagation(); setFiles(files.filter((_, j) => j !== i)); }}
                        style={{ color: "var(--mute)", fontSize: 14 }}>×</button>
                    </div>
                  ))}
                  <div className="form-field" style={{ marginTop: 20 }}>
                    <label>{isZh ? "报告标题 · 必填" : "Report title · required"}</label>
                    <input type="text" value={title} onChange={(e) => setTitle(e.target.value)}
                      placeholder={isZh ? "报告标题" : "Report title"} />
                  </div>
                  <div className="form-field" style={{ marginTop: 14 }}>
                    <label>{isZh ? "附言 · 必填" : "Note · required"}</label>
                    <textarea rows="2" value={note} onChange={(e) => setNote(e.target.value)}
                      placeholder={isZh ? "想让我们重点关注什么？" : "Anything we should focus on?"} />
                  </div>
                  {submitState.error && (
                    <div style={{ color: "#d66", fontSize: 13, marginTop: 8 }}>{submitState.error}</div>
                  )}
                  <button className="btn" style={{ marginTop: 16, width: "100%", justifyContent: "center" }}
                    disabled={submitState.busy}
                    onClick={(e) => { e.stopPropagation(); submitFiles(); }}>
                    {submitState.busy
                      ? (isZh ? "提交中…" : "Submitting…")
                      : user
                        ? <>{isZh ? "提交资料" : "Submit"} · {files.length} {t('studio.t007')}<span className="arr"></span></>
                        : <>{isZh ? "登录后提交" : "Sign in to submit"}<span className="arr"></span></>}
                  </button>
                </div>
              )}

              {/* 提交成功提示 */}
              {submitState.ok && files.length === 0 && (
                <div style={{ marginTop: 28, border: "1px solid var(--gold)", padding: 24, display: "flex", alignItems: "center", gap: 16 }}>
                  <span style={{ color: "var(--gold)", fontSize: 20 }}>✓</span>
                  <div>
                    <div style={{ fontFamily: "var(--f-serif-cn)", fontSize: 16 }}>
                      {isZh ? "资料已提交" : "Materials received"}
                    </div>
                    <div className="mono" style={{ color: "var(--mute)", fontSize: 11, marginTop: 4, letterSpacing: "0.1em" }}>
                      REF · KEXI-ST-{String(submitState.ok).padStart(6, "0")} · {isZh ? "完成后报告将出现在右侧「我的报告」" : "Your report will appear under My Reports"}
                    </div>
                  </div>
                </div>
              )}

              {/* Format chips */}
              <div style={{ marginTop: 32, display: "flex", flexWrap: "wrap", gap: 8 }}>
                {["PDF", "DOCX", "PPTX", "XLSX", "MP3", "Markdown"].map(fmt => (
                  <span key={fmt} style={{
                    fontFamily: "var(--f-mono)", fontSize: 11,
                    padding: "6px 12px",
                    border: "1px solid var(--line)",
                    color: "var(--mute)",
                    letterSpacing: "0.1em",
                  }}>{fmt}</span>
                ))}
              </div>
            </div>

            {/* My reports */}
            <div style={{ background: "var(--bg-0)", border: "1px solid var(--line)", padding: 32 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
                <div className="eyebrow">{si18.myReports}</div>
                {user ? (
                  <div className="mono" style={{ color: "var(--mute-2)", fontSize: 10, letterSpacing: "0.1em", display: "flex", gap: 12, alignItems: "center" }}>
                    <span style={{ color: "var(--gold)" }}>{user.display_name}</span>
                    <a style={{ cursor: "pointer" }} onClick={() => window.requestLogout(() => logout())}>{isZh ? "退出" : "Sign out"}</a>
                  </div>
                ) : (
                  <div className="mono" style={{ color: "var(--mute-2)", fontSize: 10, letterSpacing: "0.14em" }}>{t('studio.member')}</div>
                )}
              </div>

              {!user && (
                <div style={{
                  background: "var(--bg-1)",
                  border: "1px solid var(--line)",
                  padding: 32,
                  display: "flex", flexDirection: "column", alignItems: "center",
                  textAlign: "center",
                }}>
                  <div style={{
                    width: 48, height: 48,
                    border: "1px solid var(--line)",
                    borderRadius: "50%",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "var(--gold)",
                    marginBottom: 20,
                  }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.4"/>
                      <path d="M4 21c0-4 3.6-7 8-7s8 3 8 7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <div style={{ fontFamily: "var(--f-serif-cn)", fontSize: 18 }}>{t('studio.t008')}</div>
                  <p style={{ color: "var(--mute)", fontSize: 13, marginTop: 12, lineHeight: 1.6 }}>
                    {t('studio.regHint')}
                  </p>
                  <button className="text-link" style={{ marginTop: 20 }} onClick={onLoginClick}>
                    {t('studio.regLogin')} →
                  </button>
                </div>
              )}

              {user && (
                <>
                  {/* 报告列表 */}
                  {myReports === null && <div className="body-mute" style={{ fontSize: 13 }}>{isZh ? "加载中…" : "Loading…"}</div>}
                  {myReports && myReports.length === 0 && (
                    <div style={{ background: "var(--bg-1)", border: "1px solid var(--line)", padding: 24, textAlign: "center" }}>
                      <p className="body-mute" style={{ fontSize: 13, lineHeight: 1.7, margin: 0 }}>
                        {isZh ? "暂无报告 —— 提交资料后，我们的调研团队完成制作即会挂载到这里。" : "No reports yet — submit materials and your reports will appear here."}
                      </p>
                    </div>
                  )}
                  {myReports && myReports.map((r, i) => (
                    <div key={r.id} style={{
                      padding: "16px 0",
                      borderBottom: i < myReports.length - 1 ? "1px solid var(--line-2)" : "none",
                    }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
                        <div style={{ fontFamily: "var(--f-serif-cn)", fontSize: 15 }}>
                          {isZh ? r.title_zh : (r.title_en || r.title_zh)}
                        </div>
                        <span className="mono" style={{ color: "var(--mute-2)", fontSize: 10, flexShrink: 0 }}>
                          {new Date(r.created_at).toLocaleDateString("zh-CN")}
                        </span>
                      </div>
                      <div className="mono" style={{ color: "var(--mute-2)", fontSize: 10, marginTop: 6, letterSpacing: "0.08em" }}>
                        {r.report_type.toUpperCase()}{r.pages ? ` · ${r.pages}p` : r.report_count ? ` · ${r.report_count} ${isZh ? "份报告" : "reports"}` : ""}{r.sector ? ` · ${r.sector}` : ""}
                        {" · "}
                        <span style={{ color: r.visibility === "public" ? "var(--gold)" : "var(--mute-2)" }}>
                          {r.visibility === "public" ? (isZh ? "已公开" : "Public")
                            : r.visibility === "pending" ? (isZh ? "公开审核中" : "Pending review")
                            : (isZh ? "私密" : "Private")}
                        </span>
                      </div>
                      <div style={{ display: "flex", gap: 16, marginTop: 10, fontSize: 12 }}>
                        {(r.slug && r.visibility !== "public") ? (
                          // 定向报告：全局进度弹窗换一次性码后跳转（见 shell.jsx ReportOpenModal）
                          <a className="text-link" style={{ cursor: "pointer" }} onClick={() => window.openDirectedReport(r.id)}>
                            {isZh ? "查看报告" : "View report"} ↗
                          </a>
                        ) : (r.report_url || r.site_url || r.file_url) && (
                          <a className="text-link" href={r.report_url || r.site_url || r.file_url} target="_blank" rel="noopener noreferrer">
                            {r.report_type === "site" ? (isZh ? "查看小站" : "View site") : (isZh ? "查看报告" : "View report")} ↗
                          </a>
                        )}
                        {r.visibility === "private" && (
                          <a className="text-link" style={{ cursor: "pointer" }} onClick={() => setReportVis(r.id, "request_public")}>
                            {isZh ? "申请公开" : "Request public"}
                          </a>
                        )}
                        {(r.visibility === "pending" || r.visibility === "public") && (
                          <a style={{ cursor: "pointer", color: "var(--mute-2)", fontSize: 12 }} onClick={() => setReportVis(r.id, "make_private")}>
                            {isZh ? "设回私密" : "Make private"}
                          </a>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* 我的提交 */}
                  {mySubs && mySubs.length > 0 && (
                    <div style={{ marginTop: 28 }}>
                      <div className="eyebrow-mute" style={{ marginBottom: 12 }}>{isZh ? "我的提交" : "My submissions"}</div>
                      {mySubs.slice(0, 5).map((sub, i) => (
                        <div key={sub.id} style={{
                          padding: "10px 0",
                          borderBottom: i < Math.min(mySubs.length, 5) - 1 ? "1px solid var(--line-2)" : "none",
                          display: "grid", gridTemplateColumns: "1fr auto auto",
                          alignItems: "center", gap: 12,
                        }}>
                          <span style={{ fontSize: 13, color: "var(--ivory-2)" }}>
                            {sub.files.length} {isZh ? "份资料" : "files"}{sub.note ? ` · ${sub.note.slice(0, 24)}${sub.note.length > 24 ? "…" : ""}` : ""}
                          </span>
                          <span className="mono" style={{
                            fontSize: 10, letterSpacing: "0.08em",
                            color: sub.status === "done" ? "var(--gold)" : "var(--mute-2)",
                          }}>
                            {sub.status === "done" ? (isZh ? "已交付" : "Done")
                              : sub.status === "processing" ? (isZh ? "制作中" : "In progress")
                              : (isZh ? "已接收" : "Received")}
                          </span>
                          <span className="mono" style={{ color: "var(--mute-2)", fontSize: 10 }}>
                            {new Date(sub.created_at).toLocaleDateString("zh-CN")}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Public showcase —— 暂无真实公开案例时整块隐藏（不删除；有数据后自动显示） */}
      {showcase && showcase.length > 0 && (
      <section className="section" style={{ background: "var(--bg-0)" }}>
        <div className="container-wide">
          <SectionHead
            lang={lang}
            num={t('studio.num03')}
            kicker={t('studio.k3')}
            title={t('studio.t012')}
            lead={t('studio.t013')}
            action={s.intelligence?.url && (
              <a
                href={s.intelligence.url}
                target="_blank" rel="noopener noreferrer"
                className="mono"
                style={{ color: "var(--gold)", fontSize: 12, letterSpacing: "0.1em", textDecoration: "none", borderBottom: "1px solid var(--gold-dim)", paddingBottom: 3 }}
                data-cms-link="site_data.studio.intelligence.url"
              >
                {lang === "cn" ? s.intelligence.label.cn : s.intelligence.label.en} ↗
              </a>
            )}
          />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1, background: "var(--line)", border: "1px solid var(--line)" }}
            data-cms-redirect="studio-reports">
            {/* 公开展示区卡片由「智库报告」菜单维护，预览里右键给跳转提示 */}
            {(showcase && showcase.length > 0
              ? showcase.map((r) => ({
                  key: `pub-${r.id}`,
                  url: r.report_url || r.site_url || r.file_url,
                  date: r.published_at ? new Date(r.published_at).toLocaleDateString("zh-CN").replace(/\//g, ".") : "",
                  title: isZh ? r.title_zh : (r.title_en || r.title_zh),
                  sub: (isZh ? r.summary_zh : r.summary_en) || r.company || (isZh ? (r.title_en || "") : r.title_zh),
                  typeLabel: r.report_type === "site" ? (isZh ? "网站报告" : "Mini-site") : r.report_type.toUpperCase() + (isZh ? " 报告" : " report"),
                  sector: r.sector || "",
                  pages: r.pages,
                  reportCount: r.report_count,
                  featured: false,
                  badge: r.source === "kexi" ? (isZh ? "珂玺研究" : "Kexi research") : (isZh ? "会员研究" : "Member research"),
                }))
              : s.reports.map((r, i) => ({
                  key: `sample-${i}`,
                  url: null,
                  date: r.date,
                  title: lang === "cn" ? r.co.cn : r.co.en,
                  sub: lang === "cn" ? r.co.en : r.co.cn,
                  typeLabel: lang === "cn" ? r.type.cn : r.type.en,
                  sector: lang === "cn" ? r.sector.cn : r.sector.en,
                  pages: r.pages,
                  featured: r.featured,
                  badge: null,
                }))
            ).map((r) => (
              <div key={r.key} style={{
                background: "var(--bg-1)",
                padding: 28,
                cursor: r.url ? "pointer" : "default",
                transition: "background .3s",
                display: "flex", flexDirection: "column",
                minHeight: 280,
                position: "relative",
              }}
              onClick={() => { if (r.url) window.open(r.url, "_blank", "noopener"); }}
              onMouseEnter={e => e.currentTarget.style.background = "var(--bg-2)"}
              onMouseLeave={e => e.currentTarget.style.background = "var(--bg-1)"}
              >
                {r.featured && (
                  <div style={{
                    position: "absolute", top: 16, right: 16,
                    fontFamily: "var(--f-mono)", fontSize: 9,
                    color: "var(--gold)",
                    border: "1px solid var(--gold)",
                    padding: "3px 8px",
                    letterSpacing: "0.12em",
                  }}>★ FEATURED</div>
                )}
                {r.badge && (
                  <div className="mono" style={{
                    alignSelf: "flex-start", marginBottom: 14,
                    fontSize: 9, letterSpacing: "0.12em",
                    color: "var(--mute)", border: "1px solid var(--line)", padding: "3px 8px",
                  }}>{r.badge}</div>
                )}
                <div className="mono" style={{ color: "var(--gold-soft)", fontSize: 11, letterSpacing: "0.1em" }}>{r.date}</div>
                <div style={{ marginTop: 20, fontFamily: "var(--f-serif-cn)", fontSize: 22, fontWeight: 500 }}>{r.title}</div>
                <div className="serif-en" style={{ color: "var(--mute)", fontSize: 13, marginTop: 4 }}>{r.sub}</div>
                <div style={{ marginTop: 12, color: "var(--gold)", fontSize: 14, fontFamily: "var(--f-serif-cn)" }}>{r.typeLabel}</div>
                <div className="mono" style={{ color: "var(--mute-2)", fontSize: 10, letterSpacing: "0.08em", marginTop: 8 }}>{r.sector}</div>
                <div style={{ flex: 1 }}></div>
                <div style={{ marginTop: 32, paddingTop: 20, borderTop: "1px solid var(--line-2)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span className="mono" style={{ color: "var(--mute)", fontSize: 11 }}>{r.pages ? <>{r.pages} {t('studio.t014')}</> : r.reportCount ? <>{r.reportCount} {isZh ? "份报告" : "reports"}</> : ""}</span>
                  <span style={{ color: "var(--gold)", fontSize: 14 }}>{t('studio.t015')}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* CTA */}
      <section className="section">
        <div className="container" style={{ textAlign: "center" }}>
          <div className="eyebrow">{t('studio.t016')}</div>
          <h2 className="h-1" style={{ marginTop: 16 }}>{t('studio.t017')}</h2>
          <p className="body-lg body-mute" style={{ marginTop: 20, maxWidth: 660, margin: "20px auto 32px" }}>
            {t('studio.t018')}
          </p>
          <div style={{ display: "inline-flex", gap: 16 }}>
            <button className="btn" onClick={onLoginClick}>{t('studio.t010')}<span className="arr"></span></button>
            <a className="btn btn-ghost" href={`mailto:${t('studio.email')}`}><span>{t('studio.email')}</span><span className="arr"></span></a>
          </div>
        </div>
      </section>
    </div>
  );
}

// =========================================================
// BP — Business Plan submission
// =========================================================
function BpPage({ lang }) {
  const t = (keyOrCn, en) => {
    if (typeof keyOrCn === "string" && keyOrCn.indexOf(".") > 0 && window.I18N) {
      const [ns, k] = keyOrCn.split(".", 2);
      const val = window.I18N[lang] && window.I18N[lang][ns] && window.I18N[lang][ns][k];
      if (val !== undefined) return val;
    }
    // fallback: 兼容旧 t(cn, en) 调用
    return lang === "cn" ? keyOrCn : (en !== undefined ? en : keyOrCn);
  };
  const bp = D.bp;
  const isZh = lang === "cn";
  const [bpFile, setBpFile] = useStateS(null);
  const fr = useRefS(null);
  const [form, setForm] = React.useState({
    name: "", company: "", email: "", phone: "",
    sector: "", stage: "", amount: "", pitch: "", detail: "",
  });
  const [consent, setConsent] = React.useState(true);
  const [state, setState] = React.useState({ status: "idle", error: null, refId: null });
  const submitted = state.status === "success";
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submitBp = async () => {
    if (!consent) {
      setState({ status: "error", error: isZh ? "请先勾选保密承诺" : "Please accept the confidentiality note", refId: null });
      return;
    }
    if (!form.name.trim() || !form.company.trim() || !form.pitch.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) {
      setState({ status: "error", error: isZh ? "请填写姓名、公司、邮箱和一句话介绍" : "Name, company, email and one-liner are required", refId: null });
      return;
    }
    setState({ status: "submitting", error: null, refId: null });
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("sector", form.sector || (bp.sectors[0] ? (isZh ? bp.sectors[0].cn : bp.sectors[0].en) : ""));
      fd.append("stage", form.stage || (bp.stages[0] ? (isZh ? bp.stages[0].cn : bp.stages[0].en) : ""));
      fd.append("locale", lang);
      if (bpFile) fd.append("attachment", bpFile);
      const res = await fetch("/api/bp", { method: "POST", body: fd });
      const data = await res.json().catch(() => ({}));
      if (res.status === 429) {
        setState({ status: "error", error: isZh ? `请求过于频繁，请 ${data.retry_after || 60} 秒后再试` : `Too many requests; retry in ${data.retry_after || 60}s`, refId: null });
        return;
      }
      if (!res.ok) {
        const msg = data.error === "file_too_large"
          ? (isZh ? "附件不能超过 30 MB" : "Attachment must be under 30 MB")
          : data.error === "unsupported_mime"
            ? (isZh ? "附件仅支持 PDF / PPT" : "Attachment must be PDF / PPT")
            : (isZh ? "提交失败，请稍后再试" : "Submission failed — please retry");
        setState({ status: "error", error: msg, refId: null });
        return;
      }
      setState({ status: "success", error: null, refId: data.id });
    } catch (err) {
      setState({ status: "error", error: String(err), refId: null });
    }
  };

  const resetBp = () => {
    setForm({ name: "", company: "", email: "", phone: "", sector: "", stage: "", amount: "", pitch: "", detail: "" });
    setBpFile(null);
    setState({ status: "idle", error: null, refId: null });
  };

  return (
    <div className="page" data-screen-label="08 BP">
      <section style={{ paddingTop: "calc(var(--nav-h) + 120px)", paddingBottom: 80, position: "relative", overflow: "hidden" }}>
        <div style={{
          position: "absolute", right: "-60px", top: "10%",
          fontFamily: "var(--f-serif-en)", fontStyle: "italic",
          fontSize: 320, color: "transparent",
          WebkitTextStroke: "1px rgba(201,168,107,0.08)",
          pointerEvents: "none",
        }}>BP</div>
        <div className="container-wide" style={{ position: "relative" }}>
          <div className="eyebrow" style={{ marginBottom: 24 }}>{t('studio.t019')}</div>
          <h1 className="h-display">{t('studio.t020')}</h1>
          <h1 className="h-display">
            {lang === "cn"
              ? <span style={{ color: "var(--gold)" }}>{t('studio.t045')}</span>
              : <span className="serif-en" style={{ color: "var(--gold)" }}>{t('studio.t045')}</span>}
          </h1>
          <p className="body-lg body-mute" style={{ maxWidth: 720, marginTop: 36 }}>
            {lang === "cn" ? bp.intro.cn : bp.intro.en}
          </p>
        </div>
      </section>

      {/* Promises */}
      <section style={{ paddingBottom: 80 }}>
        <div className="container-wide">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 0, borderTop: "1px solid var(--line)", borderLeft: "1px solid var(--line)" }}>
            {bp.promises.map((p, i) => (
              <div key={p.num} style={{
                padding: 32,
                borderRight: "1px solid var(--line)",
                borderBottom: "1px solid var(--line)",
                minHeight: 220,
                display: "flex", flexDirection: "column", justifyContent: "space-between",
              }}>
                <div data-cpath-fixed={`site_data.bp.promises.${i}.num`} style={{ fontFamily: "var(--f-serif-en)", fontSize: 44, color: "var(--gold)", fontStyle: "italic", lineHeight: 1 }}>{p.num}</div>
                <div>
                  <div style={{ fontFamily: "var(--f-serif-cn)", fontSize: 20, fontWeight: 500 }}>{lang === "cn" ? p.cn : p.en}</div>
                  <p className="body-mute" style={{ marginTop: 12, fontSize: 13, lineHeight: 1.6 }}>{lang === "cn" ? p.body.cn : p.body.en}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="section" style={{ background: "var(--bg-0)" }}>
        <div className="container-wide" style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 80 }}>
          <div>
            <SectionHead
              lang={lang}
              title={t('studio.t021')}
            />
            <p className="body-mute" style={{ fontSize: 14, lineHeight: 1.8 }}>
              {t('studio.t022')}
            </p>

            <div style={{ marginTop: 40 }}>
              <div className="eyebrow-mute" style={{ marginBottom: 16 }}>{t('studio.t023')}</div>
              {bp.sectors.map((sec, i) => (
                <div key={i} style={{
                  padding: "12px 0",
                  borderBottom: i < bp.sectors.length - 1 ? "1px solid var(--line-2)" : "none",
                  fontSize: 14,
                  color: "var(--ivory-2)",
                  display: "flex", alignItems: "center", gap: 12,
                }}>
                  <span className="mono" style={{ color: "var(--gold)", fontSize: 10 }}>0{i + 1}</span>
                  <span>{lang === "cn" ? sec.cn : sec.en}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            {submitted ? (
              <div style={{
                background: "var(--bg-1)",
                border: "1px solid var(--gold)",
                padding: 60,
                textAlign: "center",
                minHeight: 480,
                display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center",
              }}>
                <div style={{
                  width: 80, height: 80,
                  border: "1px solid var(--gold)",
                  borderRadius: "50%",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "var(--gold)",
                  marginBottom: 28,
                }}>
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                    <path d="M8 16l5 5 11-12" stroke="currentColor" strokeWidth="1.5"/>
                  </svg>
                </div>
                <h3 className="h-2">{t('studio.t024')}</h3>
                <p className="body-mute" style={{ marginTop: 20, maxWidth: 360, lineHeight: 1.7 }}>
                  {t('studio.t025')}
                </p>
                <div className="mono" style={{ color: "var(--gold)", marginTop: 24, letterSpacing: "0.12em", fontSize: 11 }}>
                  REF · KEXI-BP-{String(state.refId || 0).padStart(6, "0")}
                </div>
                <button className="text-link" style={{ marginTop: 28 }} onClick={resetBp}>
                  {t('studio.t026')} →
                </button>
              </div>
            ) : (
              <div style={{ background: "var(--bg-1)", border: "1px solid var(--line)", padding: 48 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
                  <div className="form-field">
                    <label>{t('studio.t027')}</label>
                    <input type="text" value={form.name} onChange={(e) => upd("name", e.target.value)} required />
                  </div>
                  <div className="form-field">
                    <label>{t('studio.t028')}</label>
                    <input type="text" value={form.company} onChange={(e) => upd("company", e.target.value)} required />
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
                  <div className="form-field">
                    <label>{t('studio.t029')}</label>
                    <input type="email" value={form.email} onChange={(e) => upd("email", e.target.value)} required />
                  </div>
                  <div className="form-field">
                    <label>{t('studio.t030')}</label>
                    <input type="tel" value={form.phone} onChange={(e) => upd("phone", e.target.value)} />
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
                  <div className="form-field">
                    <label>{t('studio.t031')}</label>
                    <select value={form.sector} onChange={(e) => upd("sector", e.target.value)}>
                      {bp.sectors.map((s, i) => {
                        const v = lang === "cn" ? s.cn : s.en;
                        return <option key={i} value={v} style={{ background: "var(--bg-1)" }}>{v}</option>;
                      })}
                    </select>
                  </div>
                  <div className="form-field">
                    <label>{t('studio.t032')}</label>
                    <select value={form.stage} onChange={(e) => upd("stage", e.target.value)}>
                      {bp.stages.map((s, i) => {
                        const v = lang === "cn" ? s.cn : s.en;
                        return <option key={i} value={v} style={{ background: "var(--bg-1)" }}>{v}</option>;
                      })}
                    </select>
                  </div>
                </div>
                <div className="form-field">
                  <label>{t('studio.t033')}</label>
                  <input type="text" value={form.amount} onChange={(e) => upd("amount", e.target.value)} placeholder={t('studio.t034')} />
                </div>
                <div className="form-field">
                  <label>{t('studio.t035')}</label>
                  <input type="text" value={form.pitch} onChange={(e) => upd("pitch", e.target.value)} placeholder={t('studio.t036')} required />
                </div>
                <div className="form-field">
                  <label>{t('studio.t037')}</label>
                  <textarea rows="3" value={form.detail} onChange={(e) => upd("detail", e.target.value)} placeholder={t('studio.t038')}></textarea>
                </div>

                {/* File upload */}
                <div className="form-field">
                  <label>{t('studio.t039')}</label>
                  <input ref={fr} type="file" accept=".pdf,.ppt,.pptx" style={{ display: "none" }}
                    onChange={(e) => setBpFile(e.target.files[0])} />
                  <div onClick={() => fr.current?.click()} style={{
                    border: "1px dashed var(--line)",
                    padding: 20,
                    cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    transition: "border-color .3s",
                    background: bpFile ? "rgba(201,168,107,0.04)" : "transparent",
                  }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = "var(--gold)"}
                  onMouseLeave={e => e.currentTarget.style.borderColor = "var(--line)"}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <span className="mono" style={{ color: "var(--gold)" }}>↑</span>
                      <span style={{ fontSize: 14, color: bpFile ? "var(--ivory)" : "var(--mute)" }}>
                        {bpFile ? bpFile.name : t('studio.t040')}
                      </span>
                    </div>
                    {bpFile && <span className="mono" style={{ color: "var(--mute-2)", fontSize: 11 }}>{(bpFile.size / 1e6).toFixed(1)} MB</span>}
                  </div>
                </div>

                {/* Consent */}
                <div style={{ marginTop: 24, display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <input type="checkbox" id="bpConsent" style={{ accentColor: "var(--gold)", width: 18, height: 18, marginTop: 2, cursor: "pointer" }}
                    checked={consent} onChange={(e) => setConsent(e.target.checked)} />
                  <label htmlFor="bpConsent" style={{ fontSize: 12, color: "var(--mute)", lineHeight: 1.6, cursor: "pointer" }}>
                    {t('studio.t041')}
                  </label>
                </div>

                {state.error && (
                  <div style={{ color: "#d66", fontSize: 13, marginTop: 16 }}>{state.error}</div>
                )}

                <button className="btn" style={{ marginTop: 28, width: "100%", justifyContent: "center" }}
                  disabled={state.status === "submitting"}
                  onClick={submitBp}>
                  {state.status === "submitting" ? (isZh ? "提交中…" : "Submitting…") : t('studio.t042')}<span className="arr"></span>
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Social proof */}
      <section className="section">
        <div className="container-wide">
          <div className="eyebrow" style={{ textAlign: "center", marginBottom: 16 }}>{t('studio.t043')}</div>
          <h3 className="h-2" style={{ textAlign: "center", marginBottom: 60 }}>{t('studio.t044')}</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 0, borderTop: "1px solid var(--line)", borderLeft: "1px solid var(--line)" }}>
            {D.listed.slice(0, 8).map((c, i) => (
              <div key={i} className="logo-cell" style={{ aspectRatio: "4/3" }}>
                <div className="glyph" style={{ fontSize: 24 }}>{lang === "cn" ? c.cn : c.en}</div>
                <div className="meta-r">{c.ticker}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

Object.assign(window, { StudioPage, BpPage });
